import { Loading } from '@/components/loading/Loading';

import { UserbackProvider } from '@userback/react';
import { AnimatePresence } from 'framer-motion';
import { Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ExplorerSidebar } from './components/explorer/ResourceSidebar';
import { SharedFilesExplorer } from './components/explorer/SharedFilesExplorer';
import { FullWidth } from './layouts/main/FullWidth';
import ApprovalPage from './pages/approval/ApprovalPage';
import { Disabled } from './pages/authentication/Disabled';
import { Redirect } from './pages/authentication/Redirect';
import { SignIn } from './pages/authentication/SignIn';
import { SignOut } from './pages/authentication/SignOut';
import { SignOutRedirect } from './pages/authentication/SignOutRedirect';
import CreditCheckPage from './pages/creditCheck/customer/CreditCheckPage';
import MspCreditCheckPage from './pages/creditCheck/msp/MspCreditCheckPage';
import { CustomerBundlesPage } from './pages/customer/bundles/CustomerBundlesPage';
import { CustomerCreateBundlePage } from './pages/customer/bundles/CustomerCreateBundlePage';
import { CustomerEditBundlePage } from './pages/customer/bundles/CustomerEditBundlePage';
import { SavedAddressesPage } from './pages/customer/advanced/SavedAddressesPage';
import { FinanceModels } from './pages/customer/financeModels/FinanceModels';
import { LifecyclePolicies } from './pages/customer/lifecyclePolicies/LifecyclePolicies';
import { PurchaseOrderCatalogue } from './pages/customer/purchaseOrder/PurchaseOrderCatalogue';
import { PurchaseOrderContainer } from './pages/customer/purchaseOrder/PurchaseOrderContainer';
import { PurchaseOrderDeliveryDetailsPage } from './pages/customer/purchaseOrder/PurchaseOrderDeliveryDetailsPage';
import { PurchaseOrderLandingPage } from './pages/customer/purchaseOrder/PurchaseOrderLandingPage';
import { PurchaseOrderSelectedProducts } from './pages/customer/purchaseOrder/PurchaseOrderSeletedProducts';
import { PurchaseOrderStandardisedProducts } from './pages/customer/purchaseOrder/PurchaseOrderStandardisedProducts';

import PurchaseOrderCompletePage from './pages/customer/purchaseOrder/complete/PurchaseOrderCompletePage';
import { PurchaseOrderConfirmOrderPage } from './pages/customer/purchaseOrder/confirm/PurchaseOrderConfirmOrderPage';
import { CustomerCataloguePage } from './pages/customer/standardiseProducts/CustomerCataloguePage';
import { CustomerSelectedProductsPage } from './pages/customer/standardiseProducts/CustomerSelectedProductsPage';
import SelectStockModelPage from './pages/customer/standardiseProducts/SelectStockModelPage';
import { StockParameters } from './pages/customer/stockCalculator/StockParameters';
import { BundleStockLevels } from './pages/customer/stockCalculator/stockLevels/BundleStockLevels';
import { GeneralStockLevels } from './pages/customer/stockCalculator/stockLevels/GeneralStockLevels';
import { SummaryStockLevels } from './pages/customer/stockCalculator/stockLevels/summary/SummaryStockLevels';
import { SuccessPage } from './pages/generic/Success';
import { RedeemInvite } from './pages/invite/RedeemInvite';
import { LandingPage } from './pages/landing/LandingPage';
import { InvoicesPage } from './pages/management/InvoicesPage';
import { SubmittedRequests } from './pages/management/SubmittedRequests';
import { OffboardUserPage } from './pages/management/OffboardUser';
import { DeployInventoryPage } from './pages/management/DeployInventory';
import { StockReportingPage } from './pages/management/StockReportingPage';
import { WarrantyReplacementPage } from './pages/management/WarrantyReplacementPage';
import { AdvDeviceReplacementPage } from './pages/management/AdvDeviceReplacement';
import { TechRefreshPage } from './pages/management/TechRefresh';
import { StockProvisionPage } from './pages/management/stockProvision/StockProvisionPage';
import CreateMspPage from './pages/msps/CreateMspPage';
import EditMspPage from './pages/msps/EditMspPage';
import MspSettingsPage from './pages/msps/MspSettings';
import MspsPage from './pages/msps/MspsPage';
import CustomerPage from './pages/msps/customers/CustomerPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import NotificationSettings from './pages/admin/notifications/NotificationSettings';
import RateCardManagement from './pages/admin/rateCards/RateCardManagement';
import RateCardBulkUpload from './pages/admin/rateCards/bulk/RateCardBulkUpload';
import { SandboxTechRefresh } from './pages/admin/sandbox/SandboxTechRefresh';
import { SandboxDeployInventory } from './pages/admin/sandbox/SandboxDeployInventory';
import { SandboxOffboardUser } from './pages/admin/sandbox/SandboxOffboardUser';
import { SandboxAdvDeviceReplacement } from './pages/admin/sandbox/SandboxAdvDeviceReplacement';
import NotificationPreferences from './pages/customer/settings/NotificationPreferences';
import { CompanyOnboarding } from './pages/onboarding/company/CompanyOnboarding';
import { CompanyOnboardingContractSigned } from './pages/onboarding/company/CompanyOnboardingContractSigned';
import { CompanyOnboardingNdaSigned } from './pages/onboarding/company/CompanyOnboardingNdaSigned';
import { CompanyPurchaseOrderApproval } from './pages/onboarding/company/legacy/CompanyPurchaseOrderApproval';
import { SelfCompanyContractApproval } from './pages/onboarding/company/legacy/SelfCompanyContractApproval';
import { PartnerOnboarding } from './pages/onboarding/partner/PartnerOnboarding';
import { PartnerOnboardingContractSigned } from './pages/onboarding/partner/PartnerOnboardingContractSigned';
import { PartnerOnboardingNdaSigned } from './pages/onboarding/partner/PartnerOnboardingNdaSigned';
import { MyCompanyCatalogue, SmartCatalogue } from './pages/product/Catalogue';
import { CategoryExplorer, ProductExplorer } from './pages/product/ProductExplorer';
import { ManageFilters } from './pages/product/filters/ManageFilters';
import { CreateProduct } from './pages/product/product/CreateProduct';
import { EditProduct } from './pages/product/product/EditProduct';
import { BulkManage } from './pages/product/product/bulk/BulkUpload';
import { ResourceDirectoryPage } from './pages/resources/ResourceDirectory';
import { ResourcePlaceholder } from './pages/resources/ResourcePlaceholder';
import { FinanceModelsPage } from './pages/settings/financeModels/FinanceModelsPage';
import SubscriptionsPage from './pages/subscriptions/SubscriptionsPage';
import BundleUserGuide from './pages/userGuide/BundleUserGuide';
import CustomerCatalogueUserGuide from './pages/userGuide/CustomerCatalogueUserGuide';
import FinanceModelUserGuide from './pages/userGuide/FinanceModelUserGuide';
import StandardisedProductsUserGuide from './pages/userGuide/StandardisedProductsUserGuide';
import StockCalculatorUserGuide from './pages/userGuide/StockCalculatorUserGuide';
import { APIProvider } from './services/api/APIProvider';
import { Authenticated, AuthenticationProvider, Onboarded } from './services/authentication/AuthenticationProvider';
import { BackstageProvider } from './services/backstage/BackstageProvider';
import { AppInsightsProvider } from './services/insights/AppInsightsProvider';
import { IntercomProvider } from './services/intercom/IntercomProvider';
import { ListsProvider } from './services/lists/ListProvider';
import { PreferenceProvider } from './services/preferences/LocalPreferenceProvider';
import { GraphProvider } from './services/sidecar/GraphProvider';
import { BlobUploadProvider } from './services/upload/BlobUploadProvider';
import { OneDriveUploadProvider } from './services/upload/OneDriveUploadProvider';
import { BalanceProvider } from './utils/BalanceProvider';
import { CriticalErrorBoundary } from './utils/ErrorBoundary';

function App() {
  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <Suspense fallback={<Loading className="h-screen" />}>
          <CriticalErrorBoundary>
            <Outlet />
          </CriticalErrorBoundary>
        </Suspense>
      ),
      children: [
        {
          element: <Authenticated />,
          children: [
            {
              element: <IntercomProvider />,
              children: [
                { path: 'disabled', element: <Disabled /> },
                {
                  path: 'admin/sandbox',
                  children: [
                    { path: 'partners/:partnerId/companies/:companyId/tech-refresh', element: <SandboxTechRefresh /> },
                    { path: 'partners/:partnerId/companies/:companyId/deploy-inventory', element: <SandboxDeployInventory /> },
                    { path: 'partners/:partnerId/companies/:companyId/offboard-user', element: <SandboxOffboardUser /> },
                    { path: 'partners/:partnerId/companies/:companyId/adv-device-replacement', element: <SandboxAdvDeviceReplacement /> }
                  ]
                },
                {
                  path: 'companyOnboarding',
                  children: [
                    { path: 'msp/:mspId/customer/:customerId/ndaCreated', element: <CompanyOnboardingNdaSigned /> },
                    {
                      path: 'msp/:mspId/customer/:customerId/contractSigned',
                      element: <CompanyOnboardingContractSigned />
                    },
                    { index: true, element: <CompanyOnboarding /> }
                  ]
                },
                {
                  path: 'partnerOnboarding',
                  children: [
                    { path: 'msp/:mspId/ndaCreated', element: <PartnerOnboardingNdaSigned /> },
                    { path: 'msp/:mspId/contractSigned', element: <PartnerOnboardingContractSigned /> },
                    { index: true, element: <PartnerOnboarding /> }
                  ]
                },
                {
                  path: 'companyPurchaseOrderChangeApproval',
                  element: <MyCompanyCatalogue />,
                  children: [{ index: true, element: <CompanyPurchaseOrderApproval /> }]
                },
                {
                  path: 'companyContractChangeApproval',
                  element: <SelfCompanyContractApproval />
                },
                {
                  element: <Onboarded />,
                  children: [
                    {
                      element: <FullWidth />,
                      children: [
                        { index: true, element: <LandingPage /> },
                        { path: 'advDeviceReplacement', element: <AdvDeviceReplacementPage /> },
                        { path: 'techRefresh', element: <TechRefreshPage /> },
                        { path: 'submittedRequests', element: <SubmittedRequests /> },
                        { path: 'creditcheck', element: <CreditCheckPage /> },
                        { path: 'mspcreditcheck', element: <MspCreditCheckPage /> },
                        { path: 'financeModels', element: <FinanceModelsPage /> },
                        { path: 'approvals', element: <ApprovalPage /> },
                        { path: 'notifications', element: <NotificationsPage /> },
                        { path: 'settings/notifications', element: <NotificationPreferences /> },
                        { path: 'stockModel', element: <SelectStockModelPage /> },
                        {
                          path: 'admin',
                          children: [
                            { path: 'notifications', element: <NotificationSettings /> },
                            { path: 'rate-cards', element: <RateCardManagement /> },
                            { path: 'rate-cards/bulk-import', element: <RateCardBulkUpload /> }
                          ]
                        },
                        {
                          path: 'msps/manageMsps',
                          element: <Outlet />,
                          children: [
                            { index: true, element: <MspsPage /> },
                            {
                              path: 'create',
                              element: (
                                <GraphProvider>
                                  <ListsProvider>
                                    <CreateMspPage />
                                  </ListsProvider>
                                </GraphProvider>
                              )
                            },
                            {
                              path: ':partnerId',
                              children: [
                                { path: 'edit', element: <EditMspPage /> },
                                { path: 'settings', element: <MspSettingsPage /> },
                                { path: 'settings/notifications', element: <NotificationPreferences /> }
                              ]
                            }
                          ]
                        },
                        {
                          path: 'msps/:partnerId',
                          children: [
                            { path: 'customers', element: <CustomerPage /> },
                            {
                              path: 'companies',
                              children: [
                                { path: 'companiesList', element: <CustomerPage /> },
                                {
                                  path: ':companyId/purchaseOrders',
                                  element: <SmartCatalogue purchaseOrderMode={true} />,
                                  children: [
                                    {
                                      index: true,
                                      element: <PurchaseOrderLandingPage />
                                    }
                                  ]
                                }
                              ]
                            }
                          ]
                        },
                        { path: 'subscriptions', element: <SubscriptionsPage /> },
                        { path: '*', element: <Navigate to="/" /> }
                      ]
                    },
                    {
                      element: <FullWidth />,
                      children: [
                        {
                          element: (
                            <GraphProvider>
                              <OneDriveUploadProvider>
                                <SharedFilesExplorer>
                                  <ExplorerSidebar />
                                  <Outlet />
                                </SharedFilesExplorer>
                              </OneDriveUploadProvider>
                            </GraphProvider>
                          ),
                          children: [
                            { path: 'resources', element: <ResourcePlaceholder /> },
                            {
                              path: 'resources/:directoryId/:itemId',
                              element: <ResourceDirectoryPage />
                            }
                          ]
                        },
                        {
                          path: 'configuratorCatalogue/:categoryId?',
                          element: <SmartCatalogue />,
                          children: [
                            {
                              element: <ProductExplorer />,
                              children: [{ index: true, element: <CategoryExplorer /> }]
                            },
                            {
                              path: 'product',
                              children: [
                                { path: 'create', element: <CreateProduct /> },
                                { path: ':productId', element: <EditProduct /> },
                                { path: 'bulk', element: <BulkManage /> }
                              ]
                            },
                            { path: 'filters', element: <ManageFilters /> }
                          ]
                        },
                        {
                          path: 'msps/:partnerId',
                          children: [
                            {
                              path: 'mspCatalogue/:categoryId?',
                              element: <SmartCatalogue />,
                              children: [
                                {
                                  element: <ProductExplorer />,
                                  children: [{ index: true, element: <CategoryExplorer /> }]
                                },
                                { path: 'filters', element: <ManageFilters /> }
                              ]
                            },
                            {
                              path: 'companies/:companyId',
                              children: [
                                { path: 'settings/notifications', element: <NotificationPreferences /> },
                                {
                                  path: 'catalogue/:categoryId?',
                                  element: <SmartCatalogue />,
                                  children: [
                                    { index: true, element: <CustomerCataloguePage /> },
                                    { path: 'filters', element: <ManageFilters /> },
                                    { path: 'guide', element: <CustomerCatalogueUserGuide /> }
                                  ]
                                },
                                {
                                  path: 'bundles',
                                  element: <SmartCatalogue />,
                                  children: [
                                    { index: true, element: <CustomerBundlesPage /> },
                                    { path: ':id', element: <CustomerEditBundlePage /> },
                                    { path: 'create', element: <CustomerCreateBundlePage /> },
                                    { path: 'guide', element: <BundleUserGuide /> }
                                  ]
                                },
                                {
                                  path: 'standardise',
                                  element: <SmartCatalogue />,
                                  children: [
                                    {
                                      path: 'selectedProducts',
                                      element: <CustomerSelectedProductsPage />
                                    },
                                    {
                                      path: 'guide',
                                      element: <StandardisedProductsUserGuide />
                                    }
                                  ]
                                },
                                {
                                  path: 'purchaseOrders',
                                  element: <SmartCatalogue purchaseOrderMode={true} />,
                                  children: [
                                    {
                                      path: ':orderId',
                                      element: <PurchaseOrderContainer />,
                                      children: [
                                        {
                                          path: 'poCatalogue/:categoryId?',
                                          element: <PurchaseOrderCatalogue />
                                        },
                                        {
                                          path: 'poCatalogue/selectedProducts',
                                          element: <PurchaseOrderSelectedProducts />
                                        },
                                        {
                                          path: 'poCatalogue/standardisedProducts',
                                          element: <PurchaseOrderStandardisedProducts />
                                        },
                                        {
                                          path: 'poCatalogue/deliveryDetails',
                                          element: <PurchaseOrderDeliveryDetailsPage />
                                        },
                                        {
                                          path: 'poCatalogue/confirm',
                                          element: <PurchaseOrderConfirmOrderPage />
                                        },
                                        {
                                          path: 'complete',
                                          element: <PurchaseOrderCompletePage />
                                        }
                                      ]
                                    }
                                  ]
                                },
                                {
                                  path: 'management',
                                  element: <SmartCatalogue />,
                                  children: [
                                    {
                                      path: 'submittedRequests',
                                      element: <SubmittedRequests />
                                    },
                                    {
                                      path: 'stockProvision',
                                      element: <DeployInventoryPage />
                                    },
                                    {
                                      path: 'warrantyReplacement',
                                      element: <WarrantyReplacementPage />
                                    },
                                    {
                                      path: 'advDeviceReplacement',
                                      element: <AdvDeviceReplacementPage />
                                    },
                                    {
                                      path: 'techRefresh',
                                      element: <TechRefreshPage />
                                    },
                                    {
                                      path: 'offboard',
                                      element: <OffboardUserPage />
                                    },
                                    {
                                      path: 'deploy-inventory',
                                      element: <DeployInventoryPage />
                                    },
                                    {
                                      path: 'invoices',
                                      element: <InvoicesPage />
                                    },
                                    {
                                      path: 'stockReporting',
                                      element: <StockReportingPage />
                                    },
                                    {
                                      path: 'success',
                                      element: <SuccessPage text="Your request was successfully submitted." />
                                    }
                                  ]
                                },
                                {
                                  path: 'advanced',
                                  element: <SmartCatalogue />,
                                  children: [
                                    {
                                      path: 'financeModels',
                                      element: <FinanceModels />
                                    },
                                    {
                                      path: 'financeModelGuide',
                                      element: <FinanceModelUserGuide />
                                    },
                                    {
                                      path: 'lifecyclePolicies',
                                      element: <LifecyclePolicies />
                                    },
                                    {
                                      path: 'stockCalculator',
                                      element: <StockParameters />
                                    },
                                    {
                                      path: 'bundleStockLevels',
                                      element: <BundleStockLevels />
                                    },
                                    {
                                      path: 'generalStockLevels',
                                      element: <GeneralStockLevels />
                                    },
                                    {
                                      path: 'summaryStockLevels',
                                      element: <SummaryStockLevels />
                                    },
                                    {
                                      path: 'savedAddresses',
                                      element: <SavedAddressesPage />
                                    },
                                    {
                                      path: 'stockComplete',
                                      element: <SuccessPage text="Your stock fulfillment will begin soon." />
                                    },
                                    {
                                      path: 'stockGuide',
                                      element: <StockCalculatorUserGuide />
                                    }
                                  ]
                                }
                              ]
                            }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        { path: 'invite/:inviteId', element: <RedeemInvite /> },
        {
          path: 'auth',
          children: [
            { path: 'signout', element: <SignOut /> },
            { path: 'redirect', element: <Redirect /> },
            { path: 'signin', element: <SignIn /> },
            { path: 'signout/redirect', element: <SignOutRedirect /> }
          ]
        }
      ]
    }
  ]);

  return (
    <main className="text-primary-std">
      <Suspense fallback={<Loading className="h-screen" />}>
        <UserbackProvider token="A-RgguY5FfDh0bVr8QxNAKgc1zx">
          <BackstageProvider>
            <PreferenceProvider>
              <AppInsightsProvider>
                <AuthenticationProvider>
                  <APIProvider>
                    <BalanceProvider>
                      <BlobUploadProvider>
                        <AnimatePresence mode="wait">
                          <Toaster theme="light" key="toaster" />
                          <RouterProvider router={router} />
                        </AnimatePresence>
                      </BlobUploadProvider>
                    </BalanceProvider>
                  </APIProvider>
                </AuthenticationProvider>
              </AppInsightsProvider>
            </PreferenceProvider>
          </BackstageProvider>
        </UserbackProvider>
      </Suspense>
    </main>
  );
}

export default App;
